import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Download, Upload, HardDrive, Activity } from 'lucide-react';
import { formatBytes, formatPercent } from '@/lib/format';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { useNow } from '@/lib/hooks';

const REFETCH = 5_000;

export function QBittorrentWidget() {
  const now = useNow(1_000);
  const q = useQuery({
    queryKey: ['qbittorrent'],
    queryFn: endpoints.integrations.qbittorrent,
    refetchInterval: REFETCH,
  });
  const data = q.data?.qbittorrent;

  const downloading = data?.torrents.filter((t) => t.state === 'downloading' || t.state === 'forcedDL') ?? [];
  const seeding = data?.torrents.filter((t) => t.state === 'uploading' || t.state === 'forcedUP') ?? [];
  const active = data?.torrents.filter((t) => t.dlspeed > 0 || t.upspeed > 0) ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-3.5 w-3.5" />
          qBittorrent
        </CardTitle>
        {data && (
          <span className="text-xs text-muted-foreground">{data.version}</span>
        )}
      </CardHeader>
      <CardBody>
        {!data ? (
          <div className="text-sm text-muted-foreground">
            {q.isLoading ? 'loading…' : 'unavailable (check QBITTORRENT_BASE_URL env)'}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Tile
                label="Down"
                value={`${formatBytes(data.totals.globalDlspeed)}/s`}
                icon={Download}
                tone="emerald"
              />
              <Tile
                label="Up"
                value={`${formatBytes(data.totals.globalUpspeed)}/s`}
                icon={Upload}
                tone="sky"
              />
            </div>
            <div className="text-xs text-muted-foreground grid grid-cols-3 gap-2">
              <Stat label="torrents" value={data.torrents.length} />
              <Stat label="downloading" value={downloading.length} />
              <Stat label="seeding" value={seeding.length} />
            </div>
            {active.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                  Active transfers
                </div>
                <ul className="space-y-1.5">
                  {active.slice(0, 5).map((t) => (
                    <li key={t.hash} className="text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate flex-1 text-foreground/90">{t.name}</span>
                        <span className="tabular-nums text-muted-foreground whitespace-nowrap">
                          {t.dlspeed > 0 ? `↓ ${formatBytes(t.dlspeed)}/s` : ''}
                          {t.upspeed > 0 ? ` ↑ ${formatBytes(t.upspeed)}/s` : ''}
                        </span>
                      </div>
                      <ProgressBar value={t.progress * 100} className="mt-1" />
                      <div className="flex justify-between text-[10px] text-muted-foreground/70 mt-0.5">
                        <span>{formatPercent(t.progress * 100, 0)}</span>
                        <span>{formatBytes(t.downloaded)} / {formatBytes(t.size)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-2">
              <span className="flex items-center gap-1.5">
                <HardDrive className="h-3 w-3" />
                {formatBytes(data.totals.freeSpaceOnDisk)} free
              </span>
              <span>{q.data?.lastUpdated ? relTime(q.data.lastUpdated, now) : '—'}</span>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function Tile({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: string;
  icon: typeof Download;
  tone: 'emerald' | 'sky';
}) {
  const colour = tone === 'emerald' ? 'text-emerald-400' : 'text-sky-400';
  return (
    <div className="rounded-md border border-border p-2">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className={`text-lg font-semibold tabular-nums mt-0.5 ${colour}`}>{value}</div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div className="text-muted-foreground/70 text-[10px] uppercase tracking-wider">{label}</div>
      <div className="text-foreground font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function relTime(iso: string, now: number): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const diff = Math.max(0, Math.floor((now - t) / 1000));
  if (diff === 0) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}
